import { useEffect, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Counts from zero to `target` once `start` flips true. Anyone who asked for reduced motion
 * gets the final number immediately.
 */
export function useCountUp(target: number, start: boolean, durationMs = 1100): number {
  // Seeded with the real figure, not zero. If the observer never fires — an odd embedding, a
  // browser without IntersectionObserver — the number on screen is still the true one rather
  // than a confident "0 years caring for children".
  const [value, setValue] = useState(target);

  useEffect(() => {
    if (!start) return;

    if (prefersReducedMotion()) {
      setValue(target);
      return;
    }

    let frame = 0;
    let canceled = false;
    const began = performance.now();

    // Start the run from zero. The element is still transparent behind the reveal transition
    // at this point, so the reset is never seen.
    setValue(0);

    const tick = (now: number) => {
      if (canceled) return;
      const progress = Math.min((now - began) / durationMs, 1);
      // Ease-out cubic: quick off the mark, settles gently on the final number.
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => {
      canceled = true;
      cancelAnimationFrame(frame);
    };
  }, [target, start, durationMs]);

  return value;
}
